import React, { useState, useRef, useEffect } from 'react';
import {
  MessageCircle, Users, Search, Phone, X,
  Send, ChevronRight, RefreshCw, ShoppingCart, User, Bot, Settings
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  listConversations, getConversation, sendMessage, closeConversation,
  getConfig, type WaConversation, type WaMessage,
} from '../api/whatsapp.client';
import { useAuthToken } from '../../../lib/useAuthToken';

const fmtDate = (d?: string) => {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h`;
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
};

const STATE_COLORS: Record<string, string> = {
  IDLE:         'bg-gray-100 text-gray-600',
  GREETED:      'bg-blue-100 text-blue-700',
  MENU:         'bg-yellow-100 text-yellow-700',
  ORDER_PLACED: 'bg-emerald-100 text-emerald-700',
  CLOSED:       'bg-red-100 text-red-700',
};

function ConversationList({ selected, onSelect }: { selected?: number; onSelect: (id: number) => void }) {
  const t = useAuthToken();
  const [q, setQ] = useState('');
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['whatsapp', 'conversations', q],
    queryFn: () => listConversations({ q, active: true }),
    enabled: !!t,
    refetchInterval: 10_000, // auto-refresh cada 10s
  });
  const conversations: WaConversation[] = data?.data ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-divider flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar..."
            className="w-full rounded-full border border-divider bg-surface-muted pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <button onClick={() => refetch()} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-surface-muted transition">
          <RefreshCw size={14} className="text-muted" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-surface-muted" />)}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <MessageCircle size={40} className="text-muted opacity-30 mb-2" />
            <p className="text-sm text-muted">Sin conversaciones activas</p>
          </div>
        ) : (
          conversations.map(conv => {
            const lastMsg = conv.messages?.[0];
            return (
              <button
                key={conv.id}
                onClick={() => onSelect(conv.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-muted transition text-left border-b border-divider/30 ${selected === conv.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 font-bold text-sm">
                  {conv.customer_name?.[0]?.toUpperCase() ?? <Phone size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[color:var(--color-text)] truncate">
                      {conv.customer_name ?? conv.phone}
                    </p>
                    <span className="text-[10px] text-muted shrink-0 ml-2">{fmtDate(conv.last_message_at)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-xs text-muted truncate">{lastMsg?.content ?? conv.phone}</p>
                    <span className={`ml-2 shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${STATE_COLORS[conv.state] ?? 'bg-gray-100 text-gray-600'}`}>
                      {conv.state}
                    </span>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function ChatWindow({ conversationId, onClose }: { conversationId: number; onClose: () => void }) {
  const t = useAuthToken();
  const qc = useQueryClient();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState('');

  const { data: conv, isLoading, refetch } = useQuery({
    queryKey: ['whatsapp', 'conversation', conversationId],
    queryFn: () => getConversation(conversationId),
    enabled: !!t && !!conversationId,
    refetchInterval: 5_000,
  });

  const { data: config } = useQuery({
    queryKey: ['whatsapp', 'config'],
    queryFn: getConfig,
    enabled: !!t,
  });

  const sendMut = useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) => sendMessage(id, message),
    onSuccess: () => {
      setDraft('');
      refetch();
    },
    onError: () => toast.error('Error enviando mensaje'),
  });

  const closeMut = useMutation({
    mutationFn: closeConversation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['whatsapp', 'conversations'] });
      onClose();
      toast.success('Conversación cerrada');
    },
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conv?.messages]);

  const handleSend = () => {
    if (!draft.trim() || !conv) return;
    sendMut.mutate({ id: conv.id, message: draft.trim() });
  };

  if (isLoading || !conv) return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );

  const messages: WaMessage[] = conv.messages ?? [];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-divider px-4 py-3 bg-surface">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-green-100 text-green-700 font-bold text-sm">
          {conv.customer_name?.[0]?.toUpperCase() ?? '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-[color:var(--color-text)]">{conv.customer_name ?? conv.phone}</p>
            {config?.ai_enabled && config?.active && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">
                <Bot size={10} /> IA Activa
              </span>
            )}
          </div>
          <p className="text-xs text-muted">{conv.phone} · {conv.state}</p>
        </div>
        {/* Cart badge */}
        {(conv.cart?.length ?? 0) > 0 && (
          <div className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1">
            <ShoppingCart size={12} className="text-primary" />
            <span className="text-xs font-bold text-primary">{conv.cart!.length}</span>
          </div>
        )}
        <button
          onClick={() => closeMut.mutate(conv.id)}
          className="rounded-full border border-red-200 px-2 py-1 text-[10px] font-bold text-red-600 hover:bg-red-50 transition"
        >
          <X size={12} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {messages.length === 0 && (
          <p className="text-center text-sm text-muted py-8">Sin mensajes aún</p>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`flex items-end gap-2 ${msg.role === 'user' ? '' : 'flex-row-reverse'}`}>
            <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white text-[10px] ${msg.role === 'user' ? 'bg-green-500' : 'bg-primary'}`}>
              {msg.role === 'user' ? <User size={10} /> : <Bot size={10} />}
            </div>
            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
              msg.role === 'user'
                ? 'bg-white border border-divider text-[color:var(--color-text)] rounded-bl-sm'
                : 'bg-primary text-white rounded-br-sm'
            }`}>
              <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              <p className={`mt-1 text-[10px] ${msg.role === 'user' ? 'text-muted' : 'text-white/60'}`}>
                {new Date(msg.sent_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-divider px-4 py-3 bg-surface">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Escribe un mensaje manual..."
            className="flex-1 rounded-full border border-divider bg-surface-muted px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            onClick={handleSend}
            disabled={!draft.trim() || sendMut.isPending}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 transition"
          >
            <Send size={14} />
          </button>
        </div>
        <p className="mt-1 text-center text-[10px] text-muted">Mensaje enviado como admin (no pasa por el bot)</p>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
const WhatsappMonitorPage: React.FC = () => {
  const [selectedId, setSelectedId] = useState<number | undefined>();

  return (
    <div className="flex h-screen overflow-hidden bg-[color:var(--color-bg)]">
      {/* Sidebar: conversation list */}
      <div className="w-80 min-w-[280px] border-r border-divider bg-surface flex flex-col">
        <div className="px-4 pt-5 pb-3 border-b border-divider">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle size={20} className="text-green-500" />
              <h1 className="text-base font-bold text-[color:var(--color-text)]">WhatsApp</h1>
            </div>
            <Link
              to="/whatsapp/config"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted text-muted hover:text-primary transition"
              title="Configuración de la API"
            >
              <Settings size={16} />
            </Link>
          </div>
          <p className="text-xs text-muted mt-0.5">Monitor de conversaciones</p>
        </div>
        <div className="flex-1 overflow-hidden">
          <ConversationList selected={selectedId} onSelect={setSelectedId} />
        </div>
      </div>

      {/* Main: chat window */}
      <div className="flex-1 flex flex-col">
        {selectedId ? (
          <ChatWindow
            key={selectedId}
            conversationId={selectedId}
            onClose={() => setSelectedId(undefined)}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-center p-8">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <MessageCircle size={40} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-[color:var(--color-text)]">Monitor WhatsApp</h2>
            <p className="mt-2 text-sm text-muted max-w-sm">
              Selecciona una conversación para verla y responder manualmente. El bot responde automáticamente en tiempo real.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsappMonitorPage;
